// Solomon Portal v2.x \u2014 ConversationListTable.
//
// Renders one page of conversations using TanStack Table v8 in
// `manualPagination` + `manualFiltering` mode (the Edge Function does the
// filter/page work; this component is purely presentational).
//
// References:
//   - https://tanstack.com/table/v8/docs/framework/react/react-table
//   - https://tanstack.com/table/v8/docs/guide/pagination#manual-server-side-pagination
//
// Accessibility:
//   - Wrapped in a <section role="region" aria-labelledby="\u2026" aria-busy=\u2026>.
//   - Each <tr> in the body is a button-pressable row (Enter/Space) and
//     navigates via react-router on click; we use `onKeyDown` to keep the
//     keyboard surface symmetrical with the click surface.
//   - Empty states are split between "filtered" and "first-load empty" by
//     the consumer; this component only renders the table shell.

import * as React from 'react';
import { useNavigate } from 'react-router';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import type { ConversationListItem } from '../../lib/conversation-list-api';

interface ConversationListTableProps {
  items: ConversationListItem[];
  loading: boolean;
  /** Stable id for the section's accessible name. */
  labelId: string;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatCustomer(c: ConversationListItem['primaryCustomer']): string {
  if (!c) return '\u2014';
  const parts = [c.firstName, c.lastName].filter(
    (p): p is string => typeof p === 'string' && p.length > 0,
  );
  return parts.length > 0 ? parts.join(' ') : '\u2014';
}

function statusBadgeVariant(status: string): 'active' | 'closed' | 'pending' | 'outline' {
  const s = status.toLowerCase();
  if (s === 'active' || s === 'open') return 'active';
  if (s === 'closed') return 'closed';
  if (s === 'pending') return 'pending';
  return 'outline';
}

const columns: ColumnDef<ConversationListItem>[] = [
  {
    accessorKey: 'subject',
    header: 'Subject',
    cell: ({ row }) => {
      const subject = row.original.subject || '(No subject)';
      return (
        <div className="flex flex-col">
          <span className="font-medium text-foreground">{subject}</span>
          <span className="text-[12px] text-text-muted">#{row.original.number}</span>
        </div>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant={statusBadgeVariant(row.original.status)}>{row.original.status}</Badge>
    ),
  },
  {
    accessorKey: 'primaryCustomer',
    header: 'From',
    cell: ({ row }) => <span>{formatCustomer(row.original.primaryCustomer)}</span>,
  },
  {
    accessorKey: 'threadCount',
    header: 'Replies',
    cell: ({ row }) => <span>{row.original.threadCount}</span>,
  },
  {
    accessorKey: 'modifiedAt',
    header: 'Last activity',
    cell: ({ row }) => (
      <time dateTime={row.original.modifiedAt} className="text-text-body">
        {formatDate(row.original.modifiedAt)}
      </time>
    ),
  },
];

const SKELETON_ROWS = 5;

export function ConversationListTable({
  items,
  loading,
  labelId,
}: ConversationListTableProps): React.ReactNode {
  const navigate = useNavigate();
  // TanStack Table v8 returns plain functions on the table instance that
  // mutate internal state on call; the React-Compiler-aware ESLint plugin
  // (react-hooks/incompatible-library) flags this as un-memoizable, but the
  // library is the canonical server-side table primitive and the warning is
  // informational — it just disables compiler memoization for this scope.
  // We don't pass `table.*` callables into a memoized boundary, so there's
  // no stale-UI risk. See ADR-025 for the adoption rationale.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
  });

  return (
    <section
      role="region"
      aria-labelledby={labelId}
      aria-busy={loading ? 'true' : 'false'}
      className="rounded-sm border border-border-light bg-white shadow-card"
    >
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((h) => (
                <TableHead key={h.id} scope="col">
                  {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {loading
            ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <TableRow key={`skeleton-${String(i)}`}>
                  {columns.map((_c, j) => (
                    <TableCell key={`skeleton-${String(i)}-${String(j)}`}>
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : table.getRowModel().rows.map((row) => {
                const conv = row.original;
                const href = `/support/${String(conv.number)}`;
                const onActivate = (): void => {
                  void navigate(href);
                };
                return (
                  <TableRow
                    key={row.id}
                    role="link"
                    tabIndex={0}
                    aria-label={`Open conversation: ${conv.subject || '(No subject)'}`}
                    onClick={onActivate}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onActivate();
                      }
                    }}
                    className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
        </TableBody>
      </Table>
    </section>
  );
}
